import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { noop } from 'ui-components/default-props/noop';

import styles from './carrier-update-modal.module.css';
import { WalletUpdateModalBody } from './modal-body';
import { WalletUpdateModalFooter } from './wallet-update-modal-footer';
import { ModalPortal } from '../../../components/modal-portal';
import { useOnWalletRegistrationOrUpdateModalClose, useOnWalletRegistrationOrUpdateModalCancel } from '../../hooks/use-on-wallet-modal';
import { useWallet } from '../../hooks/use-wallet';
import { messages } from '../../messages';
import { LoadingBody } from '../wallet-registration/components/loading-body';
import { WalletRegistrationErrorContextProvider } from '../wallet-registration/contexts/wallet-registration-error-context';
import { WalletRegistrationFormContextProvider } from '../wallet-registration/contexts/wallet-registration-form-context';
import { WalletRegistrationModalContextProvider } from '../wallet-registration/contexts/wallet-registration-modal-context';
import { useEligibleRequirements } from '../wallet-registration/hooks/use-eligible-requirements';

export const WalletUpdateModal = ({ walletId: walletIdProp, labelPurchaseCallback = noop }) => {
  const [canProgress, setCanProgress] = useState(false);

  const { walletId } = useWallet(walletIdProp);
  const onClose = useOnWalletRegistrationOrUpdateModalClose();
  const onCancel = useOnWalletRegistrationOrUpdateModalCancel(walletId);

  const { loading, consolidatedRequirements, consolidatedTerms, consolidatedNonDefaultTerms, walletToRegister, initialized } =
    useEligibleRequirements(walletId);

  const ready = !!walletToRegister && (!loading || initialized);
  const { newResourceUpdates = [], newLabelProviderIds = [], newInsuranceProviderIds = [] } = walletToRegister ?? {};

  return (
    <ModalPortal
      confirmCancelBodyMessage={messages.exitWalletRegistrationBodyText}
      confirmCancelButtonMessage={messages.exitWalletRegistrationButtonText}
      confirmCancelHeaderMessage={messages.exitWalletRegistrationText}
      onCancel={onCancel}
      onClose={onClose}
    >
      <Modal size='medium'>
        <ModalHeader className={styles['modal-header']}>
          <FormattedMessage {...messages.updateCarriersHeader} />
        </ModalHeader>
        {ready ? (
          <WalletRegistrationFormContextProvider
            consolidatedTerms={consolidatedTerms}
            nonDefaultTerms={consolidatedNonDefaultTerms}
            registrationRequirements={consolidatedRequirements}
            walletToRegister={walletToRegister}
          >
            <WalletRegistrationModalContextProvider
              closeWalletModal={onClose}
              isUpdate
              labelPurchaseCallback={labelPurchaseCallback}
              nonDefaultTerms={consolidatedNonDefaultTerms}
              registrationRequirements={consolidatedRequirements}
              terms={consolidatedTerms}
              walletToRegister={walletToRegister}
            >
              <WalletRegistrationErrorContextProvider>
                <ModalBody>
                  <WalletUpdateModalBody
                    labelProviderIdsToRegister={newLabelProviderIds}
                    newInsuranceProviderIds={newInsuranceProviderIds}
                    newResourceUpdates={newResourceUpdates}
                    setCanProgress={setCanProgress}
                  />
                </ModalBody>
                <WalletUpdateModalFooter canProgress={canProgress} />
              </WalletRegistrationErrorContextProvider>
            </WalletRegistrationModalContextProvider>
          </WalletRegistrationFormContextProvider>
        ) : (
          <LoadingBody />
        )}
      </Modal>
    </ModalPortal>
  );
};

WalletUpdateModal.propTypes = {
  walletId: PropTypes.string,
  labelPurchaseCallback: PropTypes.func,
};
